import StoryboardIcon from "@/assets/icons/ui/storyboard.svg";
import ActiveTimelineIcon from "@/assets/icons/ui/timeline-active.svg";
import TimelineIcon from "@/assets/icons/ui/timeline.svg";
import { AnimatedCircleHighlight } from "@/component/AnimatedCircleHighlight";
import Button from "@/component/Button";
import { Tooltip as VTooltip } from "@/component/Tooltip";
import {
  SECTION_IDS,
  useArtifactsViewerData,
} from "@/hooks/useArtifactsViewerData";
import { useNewSectionScrollHint } from "@/hooks/useNewSectionScrollHint";
import { useThreadStudioHint } from "@/hooks/useThreadStudioHint";
import { ArtifactsViewerViewMode } from "@/view/ArtifactsViewer/type";
import { ConfigProvider, Popover } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";

const STUDIO_HINT_TARGET_IDS: string[] = [
  SECTION_IDS.IMAGE_STORYBOARD,
  SECTION_IDS.VIDEO_STORYBOARD,
];

interface ArtifactNavigatorProps {
  threadId: string;
  scrollContainer?: HTMLElement | null;
  viewMode: ArtifactsViewerViewMode;
  onViewModeChange: (section: ArtifactsViewerViewMode) => void;
  onNavigateToSection?: (sectionId: string) => void;
  activeSectionId?: string | null;
}

export const ArtifactNavigator = observer((props: ArtifactNavigatorProps) => {
  const {
    threadId,
    scrollContainer = null,
    viewMode,
    onViewModeChange,
    onNavigateToSection,
    activeSectionId,
  } = props;
  const artifacts = useArtifactsViewerData();
  const { showScrollHint } = useNewSectionScrollHint({
    artifacts,
    scrollContainer,
  });
  const { showStudioHint, setShowStudioHint } = useThreadStudioHint(threadId);
  const [showStoryboardPopover, setShowStoryboardPopover] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleTargetIdsRef = useRef<Set<string>>(
    new Set(
      Object.values(artifacts)
        .filter(
          (artifact) =>
            artifact.visible &&
            artifact.type === ArtifactsViewerViewMode.STORYBOARD &&
            STUDIO_HINT_TARGET_IDS.includes(artifact.id)
        )
        .map((artifact) => artifact.id)
    )
  );

  useEffect(() => {
    const newArtifactIds = new Set<string>(
      Object.values(artifacts)
        .filter(
          (artifact) =>
            artifact.visible &&
            artifact.type === ArtifactsViewerViewMode.STORYBOARD
        )
        .map((artifact) => artifact.id)
    );

    const prevVisibleTargetIds = visibleTargetIdsRef.current;
    const newArtifactIdsArray = Array.from(newArtifactIds);

    const hasNewTarget =
      prevVisibleTargetIds.size > 0 &&
      newArtifactIdsArray.length > 0 &&
      newArtifactIdsArray
        .filter((id) => STUDIO_HINT_TARGET_IDS.includes(id))
        .some((id) => !prevVisibleTargetIds.has(id));

    visibleTargetIdsRef.current = newArtifactIds;

    if (
      hasNewTarget &&
      viewMode !== ArtifactsViewerViewMode.TIMELINE &&
      artifacts.shots.data.shots.length > 0
    ) {
      setShowStudioHint(true);
    }
  }, [artifacts, setShowStudioHint, viewMode]);

  // 清理 timeout 的辅助函数
  const clearNavigateTimeout = () => {
    if (navigateTimeoutRef.current) {
      clearTimeout(navigateTimeoutRef.current);
      navigateTimeoutRef.current = null;
    }
  };

  const storyboardSections = useMemo(
    () =>
      Object.entries(artifacts).filter(([, value]) => {
        return (
          value.visible && value.type === ArtifactsViewerViewMode.STORYBOARD
        );
      }),
    [artifacts]
  );

  const getLineWidth = (isActive: boolean, isHovered: boolean) => {
    // active 且没有 hover，或者被 hover 的项，宽度为 20
    if ((isActive && !isHovered) || isHovered) {
      return 20;
    }
    // 其他项宽度为 12
    return 12;
  };

  const getOpacity = (isActive: boolean, isHovered: boolean) => {
    // active 或 hover 的项透明度为 100%
    if (isActive || isHovered) {
      return 1;
    }
    // 其他项透明度为 50%
    return 0.5;
  };

  const handleStoryboardItemClick = (sectionId: string) => {
    setShowStoryboardPopover(false);
    clearNavigateTimeout();
    // 如果当前是 timeline 模式，需要先切换到 storyboard，再延时滚动
    if (viewMode === "timeline") {
      onViewModeChange(ArtifactsViewerViewMode.STORYBOARD);
      navigateTimeoutRef.current = setTimeout(() => {
        onNavigateToSection?.(sectionId);
      }, 500);
    } else {
      onNavigateToSection?.(sectionId);
    }
  };

  useEffect(() => {
    setShowStoryboardPopover(false);
  }, [viewMode]);

  // 组件卸载时清理 timeout
  useEffect(() => {
    return () => {
      clearNavigateTimeout();
    };
  }, []);

  useEffect(() => {
    if (viewMode === ArtifactsViewerViewMode.TIMELINE && showStudioHint) {
      setShowStudioHint(false);
    }
  }, [setShowStudioHint, showStudioHint, viewMode]);

  const storyboardPopoverContent = (
    <div
      className="flex flex-col verflow-auto"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {storyboardSections.map(([key, value], index) => {
        const isActive = activeSectionId === value.id;
        const isHovered = hoveredIndex === index;
        const lineWidth = getLineWidth(isActive, isHovered);
        const opacity = getOpacity(isActive, isHovered);

        return (
          <button
            key={key}
            type="button"
            className={classNames(
              "w-full flex items-center gap-3 pl-7 pr-3 py-2 text-sm text-left cursor-pointer first:pt-3 last:pb-3",
              "active:opacity-70",
              isActive || isHovered
                ? "group"
                : "group hover:[&_.line]:bg-text-primary hover:[&_.text]:text-primary"
            )}
            style={{ opacity, transition: "opacity 150ms ease-out" }}
            onMouseEnter={() => setHoveredIndex(index)}
            onClick={() => handleStoryboardItemClick(value.id)}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {storyboardSections.length > 1 && (
                <div className="w-6">
                  <div
                    className={classNames(
                      "line h-0.5 rounded-full",
                      isActive ? "bg-primary font-medium" : "bg-text-primary"
                    )}
                    style={{
                      width: lineWidth,
                      transition:
                        "width 150ms ease-out, background-color 150ms ease-out",
                    }}
                  />
                </div>
              )}
              <span
                className={classNames(
                  "text truncate transition-colors flex-1 min-w-32",
                  isActive ? "text-primary" : "text-text-primary"
                )}
              >
                {value.title}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Popover: {
            motionDurationMid: "50ms",
          },
        },
      }}
    >
      <div className="relative h-full px-3 w-[78px] shrink-0 flex items-center justify-center">
        <div className="bg-[rgba(135,135,168,0.16)] p-2 rounded-full flex flex-col items-center gap-2">
          <div className="relative flex items-center justify-center w-11 h-11">
            <Popover
              placement="right"
              trigger="hover"
              open={
                storyboardSections.length > 0 ? showStoryboardPopover : false
              }
              onOpenChange={setShowStoryboardPopover}
              content={storyboardPopoverContent}
              arrow={false}
              classNames={{
                root: "pl-3!",
                body: classNames(
                  "p-0! flex text-xs rounded-xl! bg-background-card/80! backdrop-blur-lg"
                ),
              }}
            >
              <AnimatedCircleHighlight size={44} showHighlight={showScrollHint}>
                <Button
                  type="plain"
                  className={classNames(
                    "w-11! h-11 z-10 rounded-full",
                    viewMode === "storyboard"
                      ? "bg-white text-primary"
                      : "bg-[#23232C] text-[#535382] hover:bg-[#30303B]"
                  )}
                  icon={<StoryboardIcon className="w-7 h-7" />}
                  onClick={() => {
                    clearNavigateTimeout();
                    onViewModeChange(ArtifactsViewerViewMode.STORYBOARD);
                  }}
                />
              </AnimatedCircleHighlight>
            </Popover>
          </div>

          <div className="w-5 h-px bg-white/[.08]"></div>
          <div className="relative flex items-center justify-center w-11 h-11">
            <VTooltip
              theme="light"
              placement="right"
              title={
                <div className="flex items-center gap-2">
                  <span>✨Click to preview</span>
                  <button
                    className="text-black/60 hover:text-black transition-colors text-sm leading-none cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowStudioHint(false);
                    }}
                  >
                    ×
                  </button>
                </div>
              }
              open={showStudioHint}
              align={{
                offset: [20, 0],
              }}
              bodyClassNames="font-medium!"
              rootClassName="z-50!"
            >
              <VTooltip
                theme="light"
                title="Studio"
                placement="right"
                align={{
                  offset: [20, 0],
                }}
                open={showStudioHint ? false : undefined}
              >
                <AnimatedCircleHighlight
                  size={44}
                  showHighlight={showStudioHint}
                >
                  <Button
                    type="plain"
                    className={classNames(
                      "w-11 h-11 min-h-0 z-10 rounded-full",
                      viewMode === "timeline"
                        ? "bg-white text-primary"
                        : "bg-[#23232C] text-[#535382] hover:bg-[#30303B]"
                    )}
                    icon={
                      showStudioHint ? (
                        <ActiveTimelineIcon className="w-7 h-7" />
                      ) : (
                        <TimelineIcon className="w-7 h-7" />
                      )
                    }
                    onClick={() => {
                      clearNavigateTimeout();
                      // 用户点击 timeline button 后，隐藏 studio hint
                      if (showStudioHint) {
                        setShowStudioHint(false);
                      }
                      onViewModeChange(ArtifactsViewerViewMode.TIMELINE);
                    }}
                  />
                </AnimatedCircleHighlight>
              </VTooltip>
            </VTooltip>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
});
